import type { AssetProfile } from '@akem/shared';
import {
    Box,
    Chip,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import React from 'react';
import type { WidgetProps } from '../types/index.js';

interface LegislationBill {
  name: string;
  code?: string;
  status: string;
  impact: string;
  summary: string;
  likelihood?: string;
}

interface LegislationResearch {
  bills: LegislationBill[];
  summary?: string;
}

export const LegislationWidget: React.FC<WidgetProps> = ({ widget }) => {
  const theme = useTheme();
  const profile = widget.inputs.find((i) => i.id === 'profile')?.value as AssetProfile | undefined;
  const research = profile?.research?.legislation as LegislationResearch | undefined;

  if (!research || !research.bills || research.bills.length === 0) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          No relevant legislation found.
        </Typography>
      </Box>
    );
  }

  const getImpactColor = (impact: string) => {
    const lower = impact.toLowerCase();
    if (lower.includes('high') || lower.includes('critical')) return 'error';
    if (lower.includes('medium') || lower.includes('moderate')) return 'warning';
    return 'info';
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 2 }}>
      {research.summary && (
        <Typography variant="body2" color="text.secondary" paragraph>
          {research.summary}
        </Typography>
      )}
      <Stack spacing={2}>
        {research.bills.map((bill, idx) => (
          <Box key={idx} sx={{ borderBottom: idx < research.bills.length - 1 ? '1px solid' : 'none', borderColor: 'divider', pb: idx < research.bills.length - 1 ? 2 : 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {bill.name}
                </Typography>
                {bill.code && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {bill.code}
                  </Typography>
                )}
              </Box>
              <Chip 
                label={bill.impact} 
                size="small" 
                color={getImpactColor(bill.impact)} 
                variant="outlined" 
              />
            </Stack>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {bill.summary}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Chip label={`Status: ${bill.status}`} size="small" variant="outlined" />
              {bill.likelihood && (
                <Chip label={`Likelihood: ${bill.likelihood}`} size="small" variant="outlined" />
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
